import { TRANSIENT, classifyFailure } from '../answers.js'

/**
 * DS-2 §3.9 — a failed save, said where it happened.
 *
 * THE DEFECT THIS EXISTS FOR. Driving a beep for DS-4, design hit every
 * POST /api/answers returning 500. The UI showed nothing, advanced through all
 * eleven screens normally, and ended on "That's this one done." Ten answers
 * were rejected and the participant was told she had finished. The beep row was
 * submitted carrying zero answers.
 *
 * That is worse than a lost check-in: it is a lost check-in she believes she
 * completed, so she will not go back, and the study records her as answering.
 *
 * The rules, all of them design's:
 *  - INLINE, directly beneath the item it belongs to. Never a toast, never a
 *    modal, never a message that blocks the rest of the screen. A save failure
 *    is about one answer, so it belongs against that answer and nowhere else.
 *  - THE SELECTION IS RETAINED. She does not re-answer; the control still shows
 *    what she chose. Only the delivery failed.
 *  - Colour is never the only marker (1.4.1): the left rule and the icon-free
 *    text carry it, and the message says what happened in words.
 *
 * CLASSIFY BEFORE WORDING. "Try again" appears only where trying again can
 * work. A permanent refusal says what happened and stops, because a retry
 * button that can never succeed is the oldest failure mode in this project.
 */
export function SaveFailure({ error, onRetry }) {
  if (!error) return null

  const transient = classifyFailure(error) === TRANSIENT

  return (
    <div
      role="alert"
      className={
        'mt-[var(--s-3)] border-l-2 border-[var(--critical)] ' +
        'bg-[var(--surface-sunken)] px-[var(--s-4)] py-[var(--s-3)] ' +
        'rounded-[var(--r-control)]'
      }
    >
      <p className="t-body text-[var(--ink-900)]">
        {transient
          ? // Present tense and specific: the answer exists, it is her device or
            // our server that has not caught up. Nothing here blames her and
            // nothing here asks her to answer it again.
            'This answer hasn’t reached us yet. Your choice is still here.'
          : // No promise, no retry, no instruction she cannot carry out. It also
            // does not tell her to contact anyone: there is no contact route in
            // v0 and implying one is its own false claim.
            'This answer couldn’t be saved. Your choice is still here, and the rest of your check-in is unaffected.'}
      </p>

      {transient && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-[var(--s-2)] min-h-11 font-semibold text-[var(--accent)] underline"
        >
          Try again
        </button>
      ) : null}
    </div>
  )
}
